import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { DOCUMENT_UUID } from "config/constant";
import LeaveApplicationDetail from "components/document/read/work/LeaveApplicationDetail";
import ReceiveOfficialLetterDetail from "components/document/read/work/ReceiveOfficialLetterDetail";
import SendOfficialLetterDetail from "components/document/read/work/SendOfficialLetterDetail";
import ExpenseReportDetail from "components/document/read/work/ExpenseReportDetail";
import MinutesOfMeetingDetail from "components/document/read/work/MinutesOfMeetingDetail";
import KSSDetail from "components/document/read/work/KSSDetail";
import ReportDetail from "components/document/read/work/ReportDetail";
import LetterOfApprovalDetail from "components/document/read/work/LetterOfApprovalDetail";

import DocumentDetailFooterForRegularUser from "components/document/read/common/footer/DocumentDetailFooterForRegularUser";

import useDocumentDetailQuery, {
  DocumentDetailResponseWithAny,
} from "features/document/hooks/queries/detail/useDocumentDetailQuery";
import DocumentViewer from "components/document/read/DocumentViewer";
import DocumentDetailPdfButtons from "components/document/read/common/header/DocumentDetailHeaderForRegularUser";
import { concat } from "lodash-es";
import { pdf } from "@react-pdf/renderer";

import CardLayout from "components/foundation/templates/CardLayout";
import Divider from "components/foundation/Divider";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

import Toast, { useToast } from "components/foundation/Toast";
import Button from "components/foundation/Button";
import Modal from "components/foundation/Modal/Modal";
import { useDeleteDocumentMutation } from "features/document/hooks/mutations/useDeleteDocumentMutation";
import { ROUTER_PATH } from "config/path";
import { TrashIcon } from "assets/icons";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import usePDF from "features/pdf/hooks/usePDF";
import CorpCardUsageProofDetail from "../../../../components/document/read/work/CorpCardUsageProofDetail";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Doc = React.memo(
  ({ document }: { document: DocumentDetailResponseWithAny }) => {
    return (
      <DocumentViewer
        formComponent={<Form document={document} />}
        companyName={document.companyName}
        documentClassName={document.documentClassName}
        documentApproveStatus={document.approveStatus}
        tags={concat(
          document?.tags.map((tag) => ({
            uuid: tag.tagUuid,
            name: tag.tagName,
          })),
          document?.freeTags,
        )}
        createdUserInfo={{
          name: document.createdUserDto?.name,
          signImageUrl: document.createdUserDto?.signImageUrl,
          date: document.documentCreatedAt || "",
        }}
        approvedUserInfo={{
          name: document.approvedUserDto?.name,
          signImageUrl: document.approvedUserDto?.signImageUrl,
          date: document.approveStatusUpdatedAt || "",
        }}
        attachmentList={document.attachments}
        approveComment={document.approveComment || ""}
      />
    );
  },
);
Doc.displayName = "Doc";

const WorkDetailPageForRegularMember = () => {
  const documentUuid = useParams().uuid;

  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate: deleteDocument } = useDeleteDocumentMutation(
    documentUuid || "",
  );
  const handleDeleteDocument = () => {
    deleteDocument();
    navigate(ROUTER_PATH.document.work.dashboard);
  };

  const { data: docData, refetch: refetchDocData } =
    useDocumentDetailQuery(documentUuid);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const pdfDevicePixelRatio =
    typeof window !== "undefined"
      ? Math.max(window.devicePixelRatio || 1, 2)
      : 2;

  // 화면 렌더용 로컬 PDF blob URL
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // 다운로드/공유 버튼용 S3 URL
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>("");

  const [numPages, setNumPages] = useState<number>(0);

  const { open: openToast, onOpenChange: onOpenChangeToast } = useToast();

  const { data: user } = useUserDetailQuery();
  const hasAuthorityToDelete = user?.adminType === "SUPERADMIN";

  const { uploadDocPdfAndGetS3Url } = usePDF<HTMLDivElement>();

  // 문서 로딩 성공 시 페이지 수 받아오기
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // docData 변경 시 S3 URL 동기화(기존 유지)
  useEffect(() => {
    if (docData) setCurrentPdfUrl(docData.documentFileUrl || "");
  }, [docData]);

  // 업로드(PDF 저장) 로직(기존 유지)
  const handleUploadPdf = async (fileName: string) => {
    const { data: latestDocData } = await refetchDocData();

    if (!latestDocData) {
      throw new Error("최신 문서 데이터를 가져올 수 없습니다.");
    }

    const basePdfBlob = await pdf(
      <Doc document={latestDocData as DocumentDetailResponseWithAny} />,
    ).toBlob();

    const s3Url = await uploadDocPdfAndGetS3Url(basePdfBlob)(fileName);
    setCurrentPdfUrl(s3Url);
    return s3Url;
  };

  // docData -> blob URL 생성 (모바일/PC 공통)
  const prevBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!docData) return;

    let cancelled = false;

    (async () => {
      const basePdfBlob = await pdf(
        <Doc document={docData as DocumentDetailResponseWithAny} />,
      ).toBlob();

      if (cancelled) return;

      const url = URL.createObjectURL(basePdfBlob);

      // 이전 blob URL revoke(메모리 누수 방지)
      const prev = prevBlobUrlRef.current;
      prevBlobUrlRef.current = url;
      if (prev) setTimeout(() => URL.revokeObjectURL(prev), 1000);

      setPdfUrl(url);
      setNumPages(0);
    })();

    return () => {
      cancelled = true;
    };
  }, [docData]);

  // unmount 시 마지막 blob revoke
  useEffect(() => {
    return () => {
      const last = prevBlobUrlRef.current;
      if (last) URL.revokeObjectURL(last);
    };
  }, []);

  // PC에서도 “본문만” 보기 위해 컨테이너 width 측정
  const pcPdfContainerRef = useRef<HTMLDivElement | null>(null);
  const [pcPdfWidth, setPcPdfWidth] = useState<number>(0);

  useEffect(() => {
    if (isMobile) return;

    const el = pcPdfContainerRef.current;
    if (!el) return;

    const update = () => setPcPdfWidth(Math.max(0, el.clientWidth - 16));
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, [isMobile]);

  return (
    <>
      <WrapStyle>
        {docData && (
          <CardLayout.Container style={{ width: "100%" }}>
            <PdfLayoutStyle>
              <DocumentDetailPdfButtons
                pdfUrl={currentPdfUrl ?? docData.documentFileUrl}
                pdfAvailable={docData.approveStatus === "APPROVE"}
                basePdfUrl={pdfUrl || undefined}
                attachmentUrls={docData.attachments.map(
                  (attachment) => attachment.fileUrl,
                )}
                className="pc-only"
              />
            </PdfLayoutStyle>

            <Divider className="pc-only" />

            {isMobile ? (
              pdfUrl ? (
                <Document
                  key={pdfUrl}
                  file={pdfUrl}
                  loading={null}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => console.error("PDF 로딩 오류", error)}
                >
                  {Array.from(new Array(numPages), (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      scale={2.0}
                      devicePixelRatio={pdfDevicePixelRatio}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  ))}
                </Document>
              ) : (
                <PdfPlaceholder />
              )
            ) : (
              <PdfLayoutStyle ref={pcPdfContainerRef}>
                {pdfUrl ? (
                  <Document
                    key={pdfUrl}
                    file={pdfUrl}
                    loading={null}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) =>
                      console.error("PDF 로딩 오류", error)
                    }
                  >
                    {Array.from(new Array(numPages), (_, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        width={pcPdfWidth || undefined}
                        devicePixelRatio={pdfDevicePixelRatio}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    ))}
                  </Document>
                ) : (
                  <PdfPlaceholder />
                )}
              </PdfLayoutStyle>
            )}
          </CardLayout.Container>
        )}

        {docData && (
          <DocumentDetailFooterForRegularUser
            approvedUserName={docData.approvedUserDto.name}
            approvedUserUuid={docData.approvedUserDto.uuid}
            attachments={docData.attachments}
            documentFileUrl={docData.documentFileUrl}
            freeTags={docData.freeTags}
            tags={docData.tags}
            documentCategory={docData.documentCategoryName}
            approveStatus={docData.approveStatus || ""}
            uuid={docData.uuid}
            permissionType={docData.permissionType}
            referenceUsers={docData.referenceUsers}
            onTagUpdateSuccess={() => onOpenChangeToast(true)}
            approveComment={docData.approveComment || ""}
            author={docData.createdUserDto?.uuid || ""}
            uploadDocPdfAndGetS3Url={handleUploadPdf}
          />
        )}

        {docData && (
          <DocumentDetailPdfButtons
            pdfUrl={currentPdfUrl || docData.documentFileUrl}
            pdfAvailable={docData.approveStatus === "APPROVE"}
            basePdfUrl={pdfUrl || undefined}
            attachmentUrls={docData.attachments.map(
              (attachment) => attachment.fileUrl,
            )}
            className="mobile-only"
          />
        )}

        <Modal.Root
          open={isDeleteModalOpen}
          onOpenChange={setIsDeleteModalOpen}
        >
          <Modal.Trigger asChild>
            {hasAuthorityToDelete && (
              <Button
                variant="tertiary-neg"
                size="large"
                fill
                className="mobile-only"
                leftIcon={<TrashIcon />}
                style={{ margin: "-3rem 0 1rem" }}
              >
                삭제
              </Button>
            )}
          </Modal.Trigger>
          <Modal.Layout>
            <Modal.Body
              title="문서를 삭제하시겠습니까?"
              onCancel={() => setIsDeleteModalOpen(false)}
              onConfirm={handleDeleteDocument}
            />
          </Modal.Layout>
        </Modal.Root>
      </WrapStyle>

      <Toast.Message open={openToast} onOpenChange={onOpenChangeToast}>
        태그가 수정되었습니다.
      </Toast.Message>
    </>
  );
};

export default WorkDetailPageForRegularMember;

interface FormProps {
  document: DocumentDetailResponseWithAny;
}

const Form = ({ document }: FormProps) => {
  const documentClassUuid = document.documentClassUuid;
  const documentData = document.documentData;
  const companyName = document.companyName;
  const author = document.createdUserDto.name;

  switch (documentClassUuid) {
    case DOCUMENT_UUID.WORK.classes.휴가신청서.uuid:
      return <LeaveApplicationDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes["공문 수신"].uuid:
      return <ReceiveOfficialLetterDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes["공문 발신"].uuid:
      return <SendOfficialLetterDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.품의서.uuid:
      return <LetterOfApprovalDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.지출결의서.uuid:
      return <ExpenseReportDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.회의록.uuid:
      return <MinutesOfMeetingDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.보고서.uuid:
      return <ReportDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes.KSS.uuid:
      return <KSSDetail documentData={documentData} />;
    case DOCUMENT_UUID.WORK.classes["법인카드 사용 증빙"].uuid:
      return (
        <CorpCardUsageProofDetail
          documentData={documentData}
          companyName={companyName}
          author={author}
        />
      );
  }

  return null;
};

/* 스타일 */
const WrapStyle = styled.div`
  width: 100.5rem;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 2rem;

  & > * {
    width: 100%;
  }

  ${({ theme }) => theme.media.small} {
    .react-pdf__Document,
    .react-pdf__Page {
      width: 100% !important;
    }

    .react-pdf__Page__canvas {
      width: 100% !important;
      height: auto !important;
      display: block;
    }

    background-color: ${({ theme }) => theme.color.background.white};
    width: 100%;
    flex: 1;
  }
`;

const PdfLayoutStyle = styled.div`
  max-width: 100.5rem;
  width: 100%;
  margin: 0 auto;

  .react-pdf__Document {
    width: 100%;
  }

  .react-pdf__Page {
    width: 100% !important;
    display: flex;
    justify-content: center;
    margin: 0 auto 16px;
  }

  .react-pdf__Page__canvas {
    width: 100% !important;
    height: auto !important;
    display: block;
  }
`;

const PdfPlaceholder = styled.div`
  width: 100%;
  min-height: 32rem;

  ${({ theme }) => theme.media.small} {
    min-height: 40vh;
  }
`;
